import styled, { css } from 'styled-components';

import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  height: 100%;
  padding: 40px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.01em;
`;

export const SoundToggle = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: ${radius.full};
  border: 1px solid
    ${props => (props.active ? colors.primary : colors.outlineVariant)};
  background-color: ${props =>
    props.active ? `${colors.primary}1a` : 'transparent'};
  color: ${props => (props.active ? colors.primary : colors.onSurfaceVariant)};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
  }
`;

export const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

export const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border-radius: ${radius.full};
  border: 1px solid
    ${props => (props.active ? colors.primary : colors.outlineVariant)};
  background-color: ${props => (props.active ? colors.primary : 'transparent')};
  color: ${props =>
    props.active ? colors.onPrimary : colors.onSurfaceVariant};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
  }
`;

export const Layout = styled.div`
  display: flex;
  height: calc(100vh - 200px);
  min-height: 480px;

  border-radius: ${radius.md};
  border: 1px solid ${colors.outlineVariant}40;
  overflow: hidden;
`;

export const ThreadListPane = styled.div`
  width: 320px;
  flex-shrink: 0;
  overflow-y: auto;
  background-color: ${colors.surfaceContainerLow};
  border-right: 1px solid ${colors.outlineVariant}40;
`;

export const ThreadItem = styled.button<{ active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 16px 18px;
  background-color: ${props =>
    props.active ? colors.surfaceContainerHighest : 'transparent'};
  border: none;
  border-bottom: 1px solid ${colors.outlineVariant}30;
  cursor: pointer;

  &:hover {
    background-color: ${colors.surfaceContainerHighest};
  }
`;

export const ThreadItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

export const ThreadItemName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const UnreadDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: ${radius.full};
  background-color: ${colors.primary};
  color: ${colors.onPrimary};
  font-size: 10px;
  font-weight: 800;
`;

export const ThreadItemPreview = styled.p`
  font-size: 12.5px;
  color: ${colors.onSurfaceVariant};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AssignedBadge = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 10.5px;
  font-weight: 700;
  color: ${colors.primary};
`;

export const EmptyThreadList = styled.div`
  padding: 40px 20px;
  text-align: center;
  font-size: 13px;
  color: ${colors.onSurfaceVariant};
`;

export const MessagePane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${colors.surface};
`;

export const MessagePaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid ${colors.outlineVariant}40;

  span {
    font-size: 15px;
    font-weight: 700;
    color: ${colors.onSurface};
  }
`;

export const CloseThreadButton = styled.button`
  padding: 8px 14px;
  border-radius: ${radius.full};
  border: 1px solid ${colors.outlineVariant};
  background-color: transparent;
  color: ${colors.onSurfaceVariant};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: ${colors.error};
    color: ${colors.error};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ClosedNotice = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.outlineVariant}40;
  text-align: center;
  font-size: 12.5px;
  color: ${colors.onSurfaceVariant};
`;

export const MessagesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const MessageBubble = styled.div<{ own: boolean }>`
  max-width: 60%;
  padding: 10px 14px;
  border-radius: ${radius.md};
  font-size: 13.5px;
  line-height: 1.4;

  ${props =>
    props.own
      ? css`
          align-self: flex-end;
          background-color: ${colors.primary};
          color: ${colors.onPrimary};
          border-bottom-right-radius: 4px;
        `
      : css`
          align-self: flex-start;
          background-color: ${colors.surfaceContainerHighest};
          color: ${colors.onSurface};
          border-bottom-left-radius: 4px;
        `}
`;

export const SenderName = styled.span`
  display: block;
  margin-bottom: 3px;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  opacity: 0.75;
`;

export const MessageTime = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 10px;
  opacity: 0.7;
`;

export const EmptyMessagePane = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.onSurfaceVariant};
  font-size: 13px;
`;

export const Composer = styled.form`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.outlineVariant}40;
`;

export const ComposerInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: ${radius.full};
  border: 1px solid ${colors.outlineVariant};
  background-color: ${colors.surfaceContainerLow};
  color: ${colors.onSurface};
  font-size: 13.5px;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }
`;

export const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${radius.full};
  border: none;
  background-color: ${colors.primary};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
